import axios, { AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { getConfig } from '@config/env';
import { femaCapture } from '../fema/capture';
import { UNAUTHORIZED_EVENT, type UnauthorizedEventDetail } from '../constants/events';
import { getLogoutSource } from '../lib/logoutSource';
import { buildCacheKey, DEFAULT_MAX_AGE_MS, readFromCache, writeToCache } from './cache';
import { authEndpoints, customerEndpoints, onboardingEndpoints } from './endpoints';
import type { RefreshTokenResponse } from './types';

export interface HttpRequestConfig<D = unknown> extends AxiosRequestConfig<D> {
  /**
   * When true, the request interceptor will not auto-attach the access token.
   * Useful for endpoints that require a different bearer (e.g. refresh token),
   * or when the caller sets Authorization explicitly.
   */
  skipAuth?: boolean;
}

// Type definitions
// interface _HttpConfig {
//   apiBaseUrl: string;
//   requestTimeout?: number;
// }

// interface _SafeHttpRequestOptions {
//   method: string;
//   url: string;
//   data?: any;
//   config?: AxiosRequestConfig;
// }

interface SessionExpiredPayload {
  readonly $error?: string;
  readonly _error?: string;
  readonly sessionExpired: 'Y';
  readonly errorTitle?: string;
  readonly errorMessage?: string;
}

class SessionExpiredError extends Error {
  readonly url: string | undefined;
  readonly method: string | undefined;

  constructor(message: string, url?: string, method?: string) {
    super(message);
    this.name = 'SessionExpiredError';
    this.url = url;
    this.method = method;
  }
}

const SESSION_EXPIRED_DEFAULT_MESSAGE = 'Your session has expired. Please sign in again.';

// ============================================================================
// TOKEN REFRESH MANAGEMENT
// Handles automatic token refresh when 401 errors occur
// ============================================================================

interface FailedQueueItem {
  resolve: (value: AxiosResponse | PromiseLike<AxiosResponse>) => void;
  reject: (reason?: unknown) => void;
  config: InternalAxiosRequestConfig;
}

type IeHubInternalAxiosRequestConfig = InternalAxiosRequestConfig & {
  readonly skipAuth?: boolean;
  _retry?: boolean;
};

// State for managing token refresh
let isRefreshing = false;
let failedQueue: FailedQueueItem[] = [];

const safeLocalStorageGetItem = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeLocalStorageSetItem = (key: string, value: string): void => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // ignore storage errors (e.g., blocked storage contexts)
  }
};

const safeLocalStorageRemoveItem = (key: string): void => {
  try {
    localStorage.removeItem(key);
  } catch {
    // ignore storage errors (e.g., blocked storage contexts)
  }
};

/**
 * Process the queue of failed requests after token refresh
 * @param error - Error if refresh failed, null if successful
 * @param token - New access token if refresh succeeded
 */
const processQueue = (error: Error | null, token: string | null = null): void => {
  failedQueue.forEach(({ resolve, reject, config }) => {
    if (error) {
      reject(error);
    } else if (token) {
      // Update the config with new token and retry
      config.headers.Authorization = `Bearer ${token}`;
      resolve(axios.request(config));
    }
  });
  failedQueue = [];
};

/**
 * Attempt to refresh the access token using the stored refresh token
 * This function is called directly to avoid circular imports with AuthService
 * @returns New access token if successful, null if failed
 */
const tryRefreshToken = async (): Promise<string | null> => {
  const refreshToken = safeLocalStorageGetItem('refreshToken');
  
  if (!refreshToken) {
    console.log('🔐 Token refresh: No refresh token available');
    return null;
  }

  try {
    console.log('🔐 Token refresh: Attempting to refresh access token...');
    
    // Call refresh endpoint directly to avoid circular dependency with AuthService
    // Use axios directly without interceptors to prevent infinite loops
    const config = getConfig();
    const response = await axios.post<RefreshTokenResponse>(
      `${config.api.baseURL}${authEndpoints.refresh}`,
      { refresh_token: refreshToken },
      {
        headers: {
          'Content-Type': 'application/json',
          // Backend now expects refresh token as bearer as well
          Authorization: `Bearer ${refreshToken}`,
        },
        timeout: 10000,
      }
    );

    const tokenData = response.data;

    if (tokenData.access_token) {
      // Store new tokens
      safeLocalStorageSetItem('authToken', tokenData.access_token);
      if (tokenData.refresh_token) {
        safeLocalStorageSetItem('refreshToken', tokenData.refresh_token);
      }
      
      console.log('🔐 Token refresh: Successfully refreshed tokens');
      return tokenData.access_token;
    }

    console.log('🔐 Token refresh: No access_token in response');
    return null;
  } catch (error) {
    console.log('🔐 Token refresh: Failed to refresh token', error);
    // Clear tokens on refresh failure
    safeLocalStorageRemoveItem('authToken');
    safeLocalStorageRemoveItem('refreshToken');
    return null;
  }
};

/**
 * Check if a request should skip token refresh attempt
 * (e.g., the refresh endpoint itself, or requests that already failed refresh)
 */
const shouldSkipRefresh = (config: InternalAxiosRequestConfig): boolean => {
  // Skip if this is the refresh token request itself
  if (config.url?.includes('refresh-token')) {
    return true;
  }
  // Skip if explicitly marked to skip auth
  if ((config as IeHubInternalAxiosRequestConfig).skipAuth === true) {
    return true;
  }
  // Skip if already retried
  if ((config as IeHubInternalAxiosRequestConfig)._retry === true) {
    return true;
  }
  return false;
};

// ============================================================================
// END TOKEN REFRESH MANAGEMENT
// ============================================================================

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const isSessionExpiredPayload = (value: unknown): value is SessionExpiredPayload => {
  if (!isRecord(value)) return false;

  // Standard legacy flag
  if (value.sessionExpired === 'Y') return true;

  // Java-style security exception classes (e.g. from Spring Security)
  const title = typeof value.errorTitle === 'string' ? value.errorTitle : '';
  if (title.includes('InvalidSessionException')) {
    return true;
  }

  return false;
};

const emitUnauthorizedEvent = (detail: UnauthorizedEventDetail): void => {
  if (typeof window !== 'undefined') {
    try {
      window.dispatchEvent(new CustomEvent(UNAUTHORIZED_EVENT, { detail }));
    } catch (eventError) {
      console.warn('Failed to emit unauthorized event', eventError);
    }
  }
};

const handleSessionExpiry = (response: AxiosResponse): SessionExpiredError => {
  const url = response.config?.url;
  const method = response.config?.method;
  const payload = isSessionExpiredPayload(response.data) ? response.data : undefined;
  const title = typeof payload?.errorTitle === 'string' ? payload.errorTitle : undefined;
  const message =
    typeof payload?.errorMessage === 'string' && payload.errorMessage.trim().length > 0
      ? payload.errorMessage
      : SESSION_EXPIRED_DEFAULT_MESSAGE;
  const sessionExpiredError = new SessionExpiredError(message, url, method);

  try {
    safeLocalStorageRemoveItem('authToken');
  } catch (storageError) {
    console.warn('Failed to clear auth token after session expiry', storageError);
  }

  emitUnauthorizedEvent({
    reason: message,
    ...(url !== undefined && { url }),
    ...(method !== undefined && { method }),
  });

  femaCapture('auth_session_expired', sessionExpiredError, {
    category: 'auth',
    severity: 'warning',
    context: { url, method, title },
    skipSanitization: false,
    user: {},
  });

  return sessionExpiredError;
};

// Create axios instance with base configuration
const createHttpClient = () => {
  const config = getConfig();
  
  const instance = axios.create({
    baseURL: config.api.baseURL,
    timeout: config.api.timeout,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Allowlist of cacheable endpoints (paths only, without query params)
  const CACHEABLE_ENDPOINTS: ReadonlySet<string> = new Set<string>([
    onboardingEndpoints.allAccountTypes,
    customerEndpoints.listBroadbandSuppliers,
    customerEndpoints.listLandlineSuppliers,
    customerEndpoints.listMobileSuppliers,
    // Energy companies endpoint is a function; use base path for caching
    'api/v1/ie-energy-companies-mstr',
    customerEndpoints.listLenders,
    customerEndpoints.listDigitalLenders,
    customerEndpoints.listFreepostLenders,
  ]);

  const isCacheableGet = (method: string | undefined, url: string | undefined): url is string => {
    if (typeof url !== 'string') {
      return false;
    }
    if (url.length === 0) {
      return false;
    }

    const httpMethod = (method ?? 'get').toLowerCase();
    if (httpMethod !== 'get') return false;

    // url is a relative path (validated below), ensure we compare path without query/hash
    const cleanUrl = url.split('?')[0]?.split('#')[0] ?? url;
    return CACHEABLE_ENDPOINTS.has(cleanUrl);
  };

  // Wrap adapter to add transparent localStorage caching for allowlisted GETs
  const baseAdapter = (instance.defaults.adapter ?? axios.defaults.adapter) as unknown;
  if (baseAdapter !== undefined) {
    instance.defaults.adapter = async (adapterConfig) => {
      // Enforce relative URLs only (existing validator runs in request interceptor too)
      const url = adapterConfig.url;
      const method = adapterConfig.method;
      if (isCacheableGet(method, url)) {
        const cacheKey = buildCacheKey(url, adapterConfig.params);
        const cached = readFromCache<unknown>(cacheKey, DEFAULT_MAX_AGE_MS);
        if (cached !== null) {
          const response: AxiosResponse = {
            data: cached,
            status: 200,
            statusText: 'OK',
            headers: { 'x-cache': 'hit' },
            config: adapterConfig,
            // 'request' type is not used by callers; keep minimal stub
            request: undefined as unknown as XMLHttpRequest,
          };
          return Promise.resolve(response);
        }
      }

      let networkResponse: AxiosResponse;
      if (typeof baseAdapter === 'function') {
        networkResponse = await (baseAdapter as (cfg: any) => Promise<AxiosResponse>)(adapterConfig as any);
      } else {
        // Fallback: use Axios default request with adapter unset to avoid recursion
        const { adapter: _adapter, ...rest } = adapterConfig as unknown as Record<string, unknown>;
        networkResponse = await axios.request(rest as any);
      }
      if (networkResponse && isCacheableGet(method, url) && networkResponse.status >= 200 && networkResponse.status < 300) {
        const cacheKey = buildCacheKey(url, adapterConfig.params);
        writeToCache(cacheKey, networkResponse.data);
      }
      return networkResponse;
    };
  }

  // Request interceptor
  instance.interceptors.request.use(
    (config) => {
      // Enforce relative URLs only
      if (config.url) {
        validateUrl(config.url);
      }

      // Restrict allowed methods
      const allowedMethods = ['get', 'post', 'put', 'delete', 'patch'];
      if (config.method && !allowedMethods.includes(config.method.toLowerCase())) {
        throw new Error(`HTTP method ${config.method} is not allowed`);
      }

      // Debug logging for budget updates
      if (config.url && (config.url.includes('/income/') || config.url.includes('/income-notes/')) && 
          (config.method === 'put' || config.method === 'patch')) {
        console.log('🔄 Budget API Request:', {
          url: config.url,
          method: config.method,
          data: config.data
        });
      }
      // Add auth token if available (skip for legacy endpoints)
      const skipAuth = (config as IeHubInternalAxiosRequestConfig).skipAuth === true;
      if (!skipAuth) {
        const token = safeLocalStorageGetItem('authToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }
      
      return config;
    },
    (error) => {
      femaCapture('http_request_error', error, {
        category: 'network',
        severity: 'error',
        skipSanitization: false,
        user: {},
      });
      return Promise.reject(error);
    }
  );

  // Response interceptor
  instance.interceptors.response.use(
    (response) => {
      // Log successful responses (dev only)
      if (import.meta.env.DEV) {
        console.warn('HTTP Response:', {
          status: response.status,
          url: response.config.url,
          data: response.data,
        });
      }

      if (isSessionExpiredPayload(response.data)) {
        // Special case: ignore session expired for terms acceptance and event logging to prevent auto-logout
        const url = response.config.url || '';
        const isSuppressed = url.includes('terms-and-conditions-accepted') || url.includes('ie-event-log');
        
        if (isSuppressed) {
          console.warn(`HTTP Response: Session expired payload detected for ${url}, but suppressing logout.`);
          return response;
        }

        const sessionExpiredError = handleSessionExpiry(response);
        return Promise.reject(sessionExpiredError);
      }

      return response;
    },
    async (error) => {
      const originalRequest = error.config as IeHubInternalAxiosRequestConfig;
      
      // Handle common HTTP errors
      const errorData = {
        status: error.response?.status,
        url: error.config?.url,
        method: error.config?.method,
        message: error.message,
      };

      // Handle 401 Unauthorized - attempt token refresh before logging out
      if (error.response?.status === 401) {
        // Special-case: logout should not trigger refresh/session-expired rewriting.
        // Callers (AuthService) may want to handle 401 and retry with a different token.
        if (typeof originalRequest.url === 'string' && originalRequest.url.includes('/auth/logout')) {
          return Promise.reject(error);
        }
        // Check if we should skip refresh attempt
        if (shouldSkipRefresh(originalRequest)) {
          console.log('🔐 401 Error: Skipping refresh for this request');
          return handleUnauthorizedError(error);
        }

        // If already refreshing, queue this request
        if (isRefreshing) {
          console.log('🔐 401 Error: Token refresh in progress, queuing request');
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject, config: originalRequest });
          });
        }

        // Mark as refreshing and attempt refresh
        isRefreshing = true;
        originalRequest._retry = true;

        try {
          const newToken = await tryRefreshToken();

          if (newToken) {
            // Token refreshed successfully - process queued requests and retry original
            console.log('🔐 401 Error: Token refreshed, retrying original request');
            processQueue(null, newToken);
            
            // Update the original request with new token and retry
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return instance.request(originalRequest);
          } else {
            // Refresh failed - no valid token
            console.log('🔐 401 Error: Token refresh failed, logging out');
            const logoutError = new SessionExpiredError(
              SESSION_EXPIRED_DEFAULT_MESSAGE,
              originalRequest.url,
              originalRequest.method
            );
            processQueue(logoutError);
            return handleUnauthorizedError(error);
          }
        } catch (refreshError) {
          // Refresh threw an error
          console.log('🔐 401 Error: Token refresh threw error', refreshError);
          const logoutError = new SessionExpiredError(
            SESSION_EXPIRED_DEFAULT_MESSAGE,
            originalRequest.url,
            originalRequest.method
          );
          processQueue(logoutError);
          return handleUnauthorizedError(error);
        } finally {
          isRefreshing = false;
        }
      } else if (error.response?.status >= 500) {
        // Server errors
        femaCapture('server_error', error, {
          category: 'server',
          severity: 'error',
          context: errorData,
          skipSanitization: false,
          user: {},
        });
      } else if (error.code === 'ECONNABORTED') {
        // Timeout errors
        femaCapture('request_timeout', error, {
          category: 'network',
          severity: 'warning',
          context: errorData,
          skipSanitization: false,
          user: {},
        });
      } else {
        // Other errors
        femaCapture('http_response_error', error, {
          category: 'network',
          severity: 'error',
          context: errorData,
          skipSanitization: false,
          user: {},
        });
      }

      return Promise.reject(error);
    }
  );

  /**
   * Handle unauthorized error - emit event and create SessionExpiredError
   * This is called when token refresh fails or is not possible
   */
  function handleUnauthorizedError(error: any): Promise<never> {
    const url = error.config?.url || '';
    const isSuppressed = url.includes('terms-and-conditions-accepted') || url.includes('ie-event-log');
    
    if (!isSuppressed) {
      safeLocalStorageRemoveItem('authToken');
    } else {
      console.warn(`handleUnauthorizedError: Suppressing token clear and logout event for ${url} endpoint.`);
    }

    // Show user-friendly session timeout message for 401 errors
    // (but suppress it during explicit logout flows like manual logout / account-locked logout).
    const detail: UnauthorizedEventDetail = {
      reason: SESSION_EXPIRED_DEFAULT_MESSAGE,
      ...(error.config?.url !== undefined && { url: error.config.url }),
      ...(error.config?.method !== undefined && { method: error.config.method }),
    };

    try {
      const logoutSource = getLogoutSource();
      const shouldEmitUnauthorized =
        (logoutSource === null || logoutSource === 'system') && !isSuppressed;

      if (shouldEmitUnauthorized) {
        emitUnauthorizedEvent(detail);
      }
    } catch (eventError) {
      console.warn('Failed to emit unauthorized event', eventError);
    }

    femaCapture('auth_token_expired', error, {
      category: 'auth',
      severity: 'warning',
      skipSanitization: false,
      user: {},
    });

    const sessionExpiredError = new SessionExpiredError(
      detail.reason,
      detail.url,
      detail.method
    );
    (sessionExpiredError as { cause?: unknown }).cause = error;
    (sessionExpiredError as { status?: number }).status = 401;
    return Promise.reject(sessionExpiredError);
  }

  return instance;
};

// Export singleton instance
export const httpClient = createHttpClient();

// In-flight GET de-duplication
// Ensures that identical concurrent GET requests share the same Promise
// and only hit the network once. Once resolved/rejected, the entry is removed.
const inFlightGetRequests = new Map<string, Promise<AxiosResponse<any>>>();

const normaliseParams = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(normaliseParams);
  }

  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    const sortedKeys = Object.keys(record).sort();
    const normalised: Record<string, unknown> = {};

    for (const key of sortedKeys) {
      normalised[key] = normaliseParams(record[key]);
    }

    return normalised;
  }

  return value;
};

const buildInFlightGetKey = (url: string, config?: AxiosRequestConfig): string => {
  const cleanUrl = url.split('#')[0] ?? url;

  const rawParams = config?.params;
  let paramsKey = '';

  if (rawParams instanceof URLSearchParams) {
    paramsKey = rawParams.toString();
  } else if (rawParams !== undefined) {
    try {
      paramsKey = JSON.stringify(normaliseParams(rawParams));
    } catch {
      // Fallback to string coercion if serialisation fails for any reason
      paramsKey = String(rawParams);
    }
  }

  const responseType = config?.responseType ?? '';

  return `get|${cleanUrl}|${paramsKey}|${responseType}`;
};

// Utility functions for common HTTP operations
export const httpGet = <T = any>(
  url: string,
  config: HttpRequestConfig = {}
): Promise<AxiosResponse<T>> => {
  const key = buildInFlightGetKey(url, config);
  const existing = inFlightGetRequests.get(key) as Promise<AxiosResponse<T>> | undefined;

  if (existing) {
    return existing;
  }

  const requestPromise: Promise<AxiosResponse<T>> = httpClient
    .get<T>(url, config)
    .finally(() => {
      inFlightGetRequests.delete(key);
    });

  inFlightGetRequests.set(key, requestPromise);

  return requestPromise;
};

export const httpPost = <T = any>(url: string, data?: any, config: HttpRequestConfig = {}): Promise<AxiosResponse<T>> =>
  httpClient.post<T>(url, data, config);

export const httpPut = <T = any>(url: string, data?: any, config: HttpRequestConfig = {}): Promise<AxiosResponse<T>> =>
  httpClient.put<T>(url, data, config);

export const httpDelete = <T = any>(url: string, config: HttpRequestConfig = {}): Promise<AxiosResponse<T>> =>
  httpClient.delete<T>(url, config);

export const httpPatch = <T = any>(url: string, data?: any, config: HttpRequestConfig = {}): Promise<AxiosResponse<T>> =>
  httpClient.patch<T>(url, data, config);

// URL validation for security
export const validateUrl = (url: string): boolean => {
  // Only allow relative URLs for security
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('//')) {
    throw new Error('Only relative URLs are allowed for security reasons');
  }
  
  // Basic URL format validation
  if (!url.startsWith('/')) {
    throw new Error('URL must start with "/" for relative paths');
  }
  
  return true;
};

// Safe HTTP wrapper that validates URLs
export const safeHttpRequest = async <T = any>(
  method: string,
  url: string,
  data: any = null,
  config: HttpRequestConfig = {}
): Promise<AxiosResponse<T>> => {
  validateUrl(url);
  
  const allowedMethods = ['GET', 'POST', 'PUT', 'DELETE', 'PATCH'];
  const upperMethod = method.toUpperCase();
  
  if (!allowedMethods.includes(upperMethod)) {
    throw new Error(`HTTP method ${method} is not allowed`);
  }
  
  switch (upperMethod) {
    case 'GET':
      return httpGet(url, config);
    case 'POST':
      return httpPost(url, data, config);
    case 'PUT':
      return httpPut(url, data, config);
    case 'DELETE':
      return httpDelete(url, config);
    case 'PATCH':
      return httpPatch(url, data, config);
    default:
      throw new Error(`Unsupported HTTP method: ${method}`);
  }
};

export default httpClient;
